import React, { useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import ProductContext from '../context'
import ColorInfo, { ColorInfoTag } from './ColorInfo'
import Tabs from './Tabs'
import SwatchColor from './SwatchColor'
import styles from './styles.module.scss'
import { getVariantItem } from '@helpers/filters'
import { prefixLocale } from '@helpers/env'
import Colors from './Colors'
import { FilterOption } from 'types/filters'
import { Product, ProductWithVariants } from 'types/product'
import { useBundleContext } from '../mto/BundleContext'
import { isBundleProduct } from '../mto/helpers'

const OUT_OF_STOCK_TAG = 'Out of stock'

function getProductsBySwatchCategory(
  products: Product[] = [],
  colorCategories: string[] = []
) {
  const productsBySwatchCategory: Record<string, Product[]> = {}

  colorCategories.forEach(category => {
    productsBySwatchCategory[category] = []
  })

  products.forEach(product => {
    const productCategory = product.color?.category || ''
    const list = productsBySwatchCategory[productCategory]

    if (list && productCategory) {
      list.push(product)
    } else {
      Object.values(productsBySwatchCategory)[0]?.push(product)
    }
  })

  return productsBySwatchCategory
}

type SwatchColorsProps = {
  selectedProduct: { title: string }
  products: ProductWithVariants[]
  chooseSwatch: (arg: ProductWithVariants) => void
  tags: string[]
  colorCategories: string[]
  selectedFilterOptions: FilterOption[]
  isSelectedProductOutOfStock: boolean
  variantExist: boolean
}

function SwatchColors({
  products = [],
  chooseSwatch,
  selectedProduct,
  tags,
  colorCategories,
  selectedFilterOptions,
  isSelectedProductOutOfStock,
  variantExist
}: SwatchColorsProps) {
  const swatchMapper = (product: Product, i: number) => {
    const selectedVariants =
      getVariantItem(product, selectedFilterOptions) || []

    return (
      <SwatchColor
        key={i.toString()}
        active={selectedProduct.title === product.title}
        colorName={product.title}
        color={product.color}
        productHandle={product.handle}
        onSelect={() => chooseSwatch(product)}
        isOutOfStock={variantExist && !selectedVariants.length}
      />
    )
  }

  if (colorCategories?.length > 1) {
    const productsBySwatchCategory = getProductsBySwatchCategory(
      products,
      colorCategories
    )

    return (
      <>
        {colorCategories.map(colorCategory => {
          const categoryProducts = productsBySwatchCategory[colorCategory]

          const isColorSelected = categoryProducts.find(
            product => product.title === selectedProduct?.title
          )

          return (
            !!categoryProducts?.length && (
              <div
                key={colorCategory}
                className={styles.swatchColours__categories}
              >
                <h3>
                  <span className={styles.swatchColours__category}>
                    {colorCategory}:{' '}
                  </span>
                  {isColorSelected && (
                    <span className={styles.swatchColours__colour}>
                      {selectedProduct.title}
                      <ColorInfoTag
                        productTags={isSelectedProductOutOfStock ? [] : tags}
                        customTag={
                          isSelectedProductOutOfStock
                            ? OUT_OF_STOCK_TAG
                            : undefined
                        }
                      />
                    </span>
                  )}
                </h3>
                <div className={styles.swatchColours__list}>
                  {categoryProducts.map(swatchMapper)}
                </div>
              </div>
            )
          )
        })}
      </>
    )
  }

  return <>{products?.map(swatchMapper)}</>
}

type ProductSwatchesProps = {
  selectedProduct: Product
  tags: string[]
  selectedFilterOptions: FilterOption[]
}

function ProductSwatches({
  tags,
  selectedProduct,
  selectedFilterOptions
}: ProductSwatchesProps) {
  const { state, setLastViewed } = useBundleContext()
  const isBundle = isBundleProduct(selectedProduct.product_feed?.bundle_name)
  const router = useRouter()
  const { tabs, variantExist } = useContext(ProductContext)

  const chooseSwatch = (product: ProductWithVariants) => {
    if (!product?.handle) return

    setLastViewed(product.product_type, product.handle)

    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, productHandle: product.handle }
      },
      `${prefixLocale()}${router.pathname.replace(
        '[productHandle]',
        product.handle
      )}`,
      { shallow: true }
    )
  }

  const handleTabSwitch = (tabProducts: ProductWithVariants[]) => {
    const targetType = tabProducts[0]?.product_type

    const lastViewedHandle = state.lastViewedProducts[targetType]

    if (lastViewedHandle) {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, productHandle: lastViewedHandle }
      })
      return
    }

    const fallback =
      tabProducts.find(
        p => p.title?.toLowerCase() === selectedProduct.title?.toLowerCase()
      ) || tabProducts[0]

    if (fallback?.handle) {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, productHandle: fallback.handle }
      })
    }
  }

  const selectedTab = tabs.find(
    tab => tab.product_type === selectedProduct?.product_type
  )

  const selectedVariants =
    getVariantItem(selectedProduct, selectedFilterOptions) || []

  const isSelectedProductOutOfStock = variantExist && !selectedVariants.length

  useEffect(() => {
    if (!isBundle || !selectedProduct?.handle) return

    const currentType = selectedProduct.product_type
    const existing = state.lastViewedProducts[currentType]

    // Only set if not already stored
    if (!existing) {
      setLastViewed(currentType, selectedProduct.handle)
    }
  }, [
    isBundle,
    selectedProduct.handle,
    selectedProduct.product_type,
    state.lastViewedProducts
  ])

  return tabs.length > 1 ? (
    <Tabs
      tabs={tabs}
      activeTab={selectedTab?.tab_title || ''}
      handleTabSwitch={handleTabSwitch}
      colorInfo={
        !selectedTab?.color_categories?.length ? (
          <ColorInfo
            productColor={selectedProduct?.title}
            productTags={isSelectedProductOutOfStock ? [] : tags}
            customTag={
              isSelectedProductOutOfStock ? OUT_OF_STOCK_TAG : undefined
            }
          />
        ) : null
      }
      renderColors={tab => (
        <SwatchColors
          products={tab.products}
          selectedProduct={selectedProduct}
          chooseSwatch={chooseSwatch}
          tags={tags}
          colorCategories={tab.color_categories}
          selectedFilterOptions={selectedFilterOptions}
          isSelectedProductOutOfStock={isSelectedProductOutOfStock}
          variantExist={variantExist}
        />
      )}
    />
  ) : (
    <div className={styles.swatchColours__noTabs}>
      {!selectedTab?.color_categories?.length ? (
        <ColorInfo
          productColor={selectedProduct?.title}
          productTags={isSelectedProductOutOfStock ? [] : tags}
          customTag={isSelectedProductOutOfStock ? OUT_OF_STOCK_TAG : undefined}
        />
      ) : null}
      <Colors>
        <SwatchColors
          products={selectedTab?.products || []}
          selectedProduct={selectedProduct}
          chooseSwatch={chooseSwatch}
          tags={tags}
          colorCategories={selectedTab?.color_categories || []}
          selectedFilterOptions={selectedFilterOptions}
          isSelectedProductOutOfStock={isSelectedProductOutOfStock}
          variantExist={variantExist}
        />
      </Colors>
    </div>
  )
}

export default ProductSwatches
